import {
    CrudStatus,
    FieldSchema,
    CrudStep,
    EntryStatus,
    CrudContextValues,
} from '../types';
import {Dictionary} from 'lodash';
import {scrollToBottom, scrollToTop, syncCrud} from '../functions';
import identity from 'lodash/identity';
import matchByProps from 'rodash/matchByProps';
import omit from 'lodash/omit';
import React, {Component} from 'react';
import t from 'rcm-i18n';
import CrudEditor from './CrudEditor';
import transform from 'lodash/transform';
import CrudContext from '../CrudContext';

interface Props<V extends object> {
    className?: string;

    /**
     * Initial data for the editor
     */
    data: {[id: string]: V}|V[];

    /**
     * Disables all controls in the table
     */
    disabled?: boolean;

    /**
     * Time required by filter changes to update search results
     */
    filterDebounce?: number;

    /**
     * Placeholder to put inside the filter field
     */
    filterPlaceholder?: string;

    /**
     * Called when TableEditor is ready for data to be loaded
     */
    onLoad?: () => Promise<void>;

    /**
     * Called to create a new, empty value
     */
    onEmptyCreate: (id: string) => V;

    /**
     * Fired when a new value is created
     */
    onValueCreate?: (value: V) => Promise<void>;

    /**
     * Fired when an existing value is updated
     */
    onValueUpdate?: (value: V) => Promise<void>;

    /**
     * Fired when an existing value is deleted
     */
    onValueDelete?: (value: V) => Promise<void>;

    /**
     * Schema of a single item in the table
     */
    schema: FieldSchema[];
}

interface State<V extends object> {
    creates: Dictionary<V>;
    deletes: Dictionary<V>;
    filter: string;
    nextNewId: string;
    order: {by?: keyof V, descending?: boolean};
    status: CrudStatus;
    statuses: Array<EntryStatus<V>>;
    step: CrudStep;
    updates: Dictionary<V>;
}

export default class CrudEditorContainer<V extends object>
    extends Component<Props<V>, State<V>> {

    public static contextType = CrudContext;
    public context: CrudContextValues;

    public static defaultProps: Partial<Props<object>> = {
        filterDebounce: 300,
        onLoad: () => Promise.resolve(),
        onValueCreate: () => Promise.resolve(),
        onValueDelete: () => Promise.resolve(),
        onValueUpdate: () => Promise.resolve(),
    };

    public state: State<V> = {
        creates: {},
        deletes: {},
        filter: null,
        nextNewId: '0',
        order: { by: null, descending: false },
        status: 'ready',
        statuses: [],
        step: 'edit',
        updates: {},
    };

    /**
     * User wants to make (unsaved) changes to an existing row
     */
    private handleValueChange = (v: V) => {
        const id = v[this.idName];
        this.setState(({updates}) => ({ updates: { ...updates, [id]: v } }));
    }

    /**
     * User wants to clear unsaved changes from an existing row
     */
    private handleValueReset = (v: V) => {
        const updates = omit(this.state.updates, v[this.idName]);
        this.setState({ updates });
    }

    /**
     * User marked an existing value for deletion
     */
    private handleValueRemove = (v: V) => {
        const id = v[this.idName];
        const deletes = {...this.state.deletes};
        if (deletes[id]) {
            delete deletes[id];
        } else {
            deletes[id] = v;
        }
        this.setState({ deletes });
    }

    /**
     * User changed data in a prospective row
     */
    private handleCreateChange = (v: V, newId: string) => {
        const creates = { ...this.state.creates, [newId]: v };
        this.setState({ creates });
    }

    /**
     * User wants to remove a prospective row of data
     */
    private handleCreateRemove = (newId: string) => {
        const creates = {...this.state.creates};
        delete creates[newId];
        this.setState({ creates });
    }

    /**
     * User wants to start a new, empty row of data
     */
    private handleCreateNew = () => {
        const creates = {...this.state.creates};
        creates[this.state.nextNewId] =
            this.props.onEmptyCreate(this.state.nextNewId);
        this.setState({
            creates,
            nextNewId: this.state.nextNewId + 1,
        }, scrollToBottom);
    }

    /**
     * User wants to discard all pending changes
     */
    private handleDiscardAll = () => {
        const answer = window.confirm(t(
            'Are you sure you want to discard all pending changes?'
        ));
        if (!answer) {
            return;
        }
        this.setState({
            creates: {},
            deletes: {},
            updates: {},
        });
    }

    /**
     * User changed the value in the filter box
     */
    private handleFilterChange = (filter: string) => {
        this.setState({ filter });
    }

    /**
     * User changed the sort order
     */
    private handleOrderChange = (
        order: {by: keyof V, descending: boolean}
    ) => {
        this.setState({ order });
    }

    /**
     * User wants to review changes
     */
    private handleReview = () => {
        if (this.totalEdits === 0) {
            return;
        }
        scrollToTop();
        this.setState({ step: 'review' });
    }

    /**
     * User wants to cancel review
     */
    private handleReviewCancel = () => {
        this.setState({ step: 'edit' });
    }

    private handleReviewSubmit = async () => {
        await syncCrud(
            this.setState.bind(this),
            this.idName,
            this.props.onValueCreate,
            this.props.onValueUpdate,
            this.props.onValueDelete,
            getErrorResponseMessage
        );
    }

    private handleSaveClose = async () => {
        scrollToTop();
        this.setState({ step: 'edit', status: 'loading' });
        await this.props.onLoad();
        this.setState({ status: 'loaded' });
    }

    private handleSaveStop = () => {
        this.setState({ status: 'interrupted' });
    }

    private get idName() {
        return this.props.schema[0].name;
    }

    private getValue = (id: any) => {
        return Object.values(this.props.data).find(
            (v) => `${v[this.idName]}` === `${id}`
        );
    }

    /**
     * @todo Move this logic to a public helper function
     */
    private get schemaVisible() {
        return this.props.schema.filter((f) => !f.hidden);
    }

    /**
     * @todo Move this logic to a public helper function
     */
    private get schemaVisibleDict(): Dictionary<FieldSchema> {
        return transform(this.schemaVisible, (r, f) => r[f.name] = f, {});
    }

    /**
     * @todo Move this logic to a public helper function
     */
    private get filteredValues() {
        const schema = this.schemaVisibleDict;
        const keys = Object.keys(schema);
        return Object.values(this.props.data).filter((value) => matchByProps(
            this.state.filter,
            transform(value, (r, v, k) => {
                if (schema[k as string]) {
                    r[k] = (schema[k as string].format || identity)(v);
                }
            }) as any,
            keys
        ));
    }

    /**
     * @todo Move this logic to a public helper function
     */
    private get sortedValues() {
        const byField =
            this.props.schema.find((f) => f.name === this.state.order.by);
        const format = (byField && byField.format) || identity;
        const compare = (a: V, b: V) => (
            format(a[this.state.order.by]) > format(b[this.state.order.by]) ?
                1 :
            format(a[this.state.order.by]) < format(b[this.state.order.by]) ?
                -1 :
            0
        ) * (this.state.order.descending ? -1 : 1);
        return this.filteredValues.sort(compare);
    }

    private get values() {
        return this.sortedValues.map((v) => {
            const id = v[this.idName];
            return {
                checked: !!this.state.deletes[id],
                edited: !!this.state.updates[id],
                id,
                value: this.state.updates[id] || v,
            };
        });
    }

    get disabled() {
        return this.props.disabled ||
            ['saving', 'loading'].includes(this.state.status);
    }

    private get totalEdits() {
        const {state} = this;
        return Object.keys(state.updates).length +
                Object.keys(state.creates).length +
                Object.keys(state.deletes).length;
    }

    private get labels() {
        return this.props.schema
            .filter((f) => !f.hidden)
            .map((f) => f.label)
            .join(', ');
    }

    public async componentDidMount() {
        this.setState({ status: 'loading' });
        try {
            await this.props.onLoad();
            this.setState({ status: 'loaded' });
        } catch (e) {
            console.error(e);
            this.setState({ status: 'error' });
        }
    }

    public componentDidUpdate() {
        this.context.onPreventClose(
            this.totalEdits > 0 ||
            this.state.status === 'saving'
        );
    }

    public render() {
        const {props, state} = this;
        return <CrudEditor<V>
            className={props.className}
            creates={state.creates}
            deletes={state.deletes}
            disabled={this.disabled}
            filter={state.filter}
            filterDebounce={props.filterDebounce}
            filterPlaceholder={props.filterPlaceholder}
            getValue={this.getValue}
            labels={this.labels}
            onCreateChange={this.handleCreateChange}
            onCreateNew={this.handleCreateNew}
            onCreateRemove={this.handleCreateRemove}
            onDiscardAll={this.handleDiscardAll}
            onFilterChange={this.handleFilterChange}
            onOrderChange={this.handleOrderChange}
            onReview={this.handleReview}
            onReviewCancel={this.handleReviewCancel}
            onReviewSubmit={this.handleReviewSubmit}
            onSaveClose={this.handleSaveClose}
            onSaveStop={this.handleSaveStop}
            onValueChange={this.handleValueChange}
            onValueRemove={this.handleValueRemove}
            onValueReset={this.handleValueReset}
            order={state.order}
            schema={props.schema}
            schemaVisible={this.schemaVisible}
            status={state.status}
            statuses={state.statuses}
            step={state.step}
            totalEdits={this.totalEdits}
            updates={state.updates}
            values={this.values}
        />;
    }
}

function getErrorResponseMessage(e: Error) {
    console.log(e);
    return 'An error occurred while processing this entry.';
}
